import React, {useEffect, useRef, useState} from 'react';
import {
  Box,
  Stack,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
} from '@mui/material';
import {
  Icon,
  Headline,
  Body,
  Button,
  designSystemToken,
  PoppaysLoader,
} from '@lightricks/react-design-system';
import {Campaign} from '@/types/campaign';
import {ContentSubmissionAnalysis} from '@/types/contentSubmission';
import {ProfileProps} from '@/types/creatorProfile';
import styles from '../ContentSubmissionAnalysisModal.module.scss';
import CampaignCreatorInfo from './CampaignCreatorInfo';

const COPY_CONFIRMATION_DURATION_MS = 2000;

const StyledAccordionDetails = styled(AccordionDetails)({
  'textAlign': 'left',
  '& *': {
    textAlign: 'left',
  },
});

interface TimestampSpanProps {
  timestamp?: string;
  timestampSeconds?: number;
  isVideo: boolean;
  onTimestampClick: (seconds: number) => void;
}

function TimestampSpan({
  timestamp,
  timestampSeconds,
  isVideo,
  onTimestampClick,
}: TimestampSpanProps) {
  let seconds = 0;
  if (timestampSeconds !== undefined) {
    seconds = timestampSeconds;
  } else if (timestamp) {
    seconds = parseTimestampToSeconds(timestamp);
  }
  const displayText = timestamp || formatTimestampSeconds(seconds);

  if (!isVideo) {
    return (
      <span
        style={{
          color: designSystemToken('semantic.fg.secondary'),
          marginLeft: '8px',
        }}
      >
        {displayText}
      </span>
    );
  }

  return (
    <span
      onClick={() => {
        onTimestampClick(seconds);
      }}
      style={{
        color: designSystemToken('semantic.fg.brand'),
        marginLeft: '8px',
        cursor: 'pointer',
        textDecoration: 'underline',
      }}
    >
      {displayText}
    </span>
  );
}

interface ContentSubmissionForDisplay {
  id: string;
  kind?: string;
  url?: string | null;
  thumbnailUrl?: string | null;
  thumbnailUrls?: string[];
  profile?: ProfileProps | null;
  campaign?: Campaign | null;
}

interface AnalysisContentProps {
  isLoading: boolean;
  contentSubmissionAnalysis?: ContentSubmissionAnalysis | null;
  contentSubmission?: ContentSubmissionForDisplay | null;
}

function AnalysisContent({
  isLoading,
  contentSubmissionAnalysis,
  contentSubmission,
}: AnalysisContentProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const metadataListenerRef = useRef<(() => void) | null>(null);
  const [copied, setCopied] = useState(false);

  const mediaUrl = contentSubmission?.url;
  const thumbnailUrl =
    contentSubmission?.thumbnailUrl ||
    (contentSubmission?.thumbnailUrls && contentSubmission.thumbnailUrls[0]);
  const isVideo = contentSubmission?.kind === 'video';
  const isImage = contentSubmission?.kind === 'photo';

  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video && metadataListenerRef.current) {
        video.removeEventListener(
          'loadedmetadata',
          metadataListenerRef.current
        );
        metadataListenerRef.current = null;
      }
    };
  }, [mediaUrl, isVideo]);

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <PoppaysLoader />
      </div>
    );
  }

  if (!contentSubmissionAnalysis) {
    return (
      <Box padding={4}>
        <Body size="lg">
          No analysis available for this content submission.
        </Body>
      </Box>
    );
  }

  const analysis = contentSubmissionAnalysis;
  const campaignFit = analysis.analysisResult?.campaignFit;

  const hasIssues = analysis.analysisIssues?.length > 0;
  const score = analysis.campaignFitScore ?? campaignFit?.score;
  const keyStrengths =
    analysis.keyStrengths?.length > 0
      ? analysis.keyStrengths
      : campaignFit?.keyStrengths || [];
  const analysisIssues =
    analysis.analysisIssues?.length > 0
      ? analysis.analysisIssues
      : campaignFit?.analysisIssues || [];
  const formattedIssues = analysis.formattedIssues || [];
  const themeRelevance = campaignFit?.themeRelevance || '';
  const visualStyleFit = campaignFit?.visualStyleFit || '';
  const alignmentSummary =
    analysis.alignmentSummary || campaignFit?.alignmentSummary || '';
  const audienceAlignment = campaignFit?.audienceAlignment || '';
  const areasForImprovement =
    analysis.areasForImprovement?.length > 0
      ? analysis.areasForImprovement
      : campaignFit?.areasForImprovement || [];
  const contentMandatoriesChecklist =
    analysis.contentMandatoriesChecklist?.length > 0
      ? analysis.contentMandatoriesChecklist
      : campaignFit?.contentMandatoriesChecklist || [];
  const creatorFeedbackMessage =
    analysis.creatorFeedbackMessage ||
    campaignFit?.creatorFeedbackMessage ||
    '';

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(creatorFeedbackMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_CONFIRMATION_DURATION_MS);
    });
  };

  const getScoreColor = (scoreValue: number) => {
    if (scoreValue >= 8) {
      return designSystemToken('semantic.fg.success');
    }
    if (scoreValue >= 6) {
      return designSystemToken('semantic.fg.warning');
    }
    return designSystemToken('semantic.fg.danger');
  };

  const expandIcon = (
    <Icon name="Actions-Arrow-Down-Large" size="small" appearance="primary" />
  );

  const seekToTime = (video: HTMLVideoElement, timestampSeconds: number) => {
    const {duration} = video;
    if (Number.isNaN(duration) || duration === 0) {
      return false;
    }

    const safeTime = Math.min(Math.max(0, timestampSeconds), duration);
    video.currentTime = safeTime;
    video.play().catch(() => {
      // Ignore play errors (autoplay restrictions)
    });
    return true;
  };

  const handleTimestampClick = (timestampSeconds: number) => {
    if (!videoRef.current || !isVideo) {
      return;
    }

    const video = videoRef.current;

    if (metadataListenerRef.current) {
      video.removeEventListener('loadedmetadata', metadataListenerRef.current);
      metadataListenerRef.current = null;
    }

    if (video.readyState >= 1) {
      seekToTime(video, timestampSeconds);
    } else {
      const onMetadataLoaded = () => {
        seekToTime(video, timestampSeconds);
        metadataListenerRef.current = null;
      };
      metadataListenerRef.current = onMetadataLoaded;
      video.addEventListener('loadedmetadata', onMetadataLoaded, {once: true});
      if (video.readyState === 0) {
        video.load();
      }
    }
  };

  let analysisIssuesList: React.ReactNode;
  if (formattedIssues.length > 0) {
    analysisIssuesList = formattedIssues.map((issue, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{display: 'flex', gap: 1}}>
        <Body size="md" color={designSystemToken('semantic.fg.primary')}>
          • {issue.description}
          {issue.timestamp && (
            <TimestampSpan
              timestamp={issue.timestamp}
              isVideo={isVideo}
              onTimestampClick={handleTimestampClick}
            />
          )}
        </Body>
      </Box>
    ));
  } else {
    analysisIssuesList = analysisIssues.map((issue, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <Box key={index} sx={{display: 'flex', gap: 1}}>
        <Body size="md" color={designSystemToken('semantic.fg.primary')}>
          • {issue.description}
          {issue.timestampSeconds !== undefined && (
            <TimestampSpan
              timestampSeconds={issue.timestampSeconds}
              isVideo={isVideo}
              onTimestampClick={handleTimestampClick}
            />
          )}
        </Body>
      </Box>
    ));
  }

  return (
    <Box padding={3}>
      <Headline size="lg" color={designSystemToken('semantic.fg.primary')}>
        AI Content Analysis
      </Headline>
      <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
        Automated campaign fit review powered by AI
      </Body>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 3,
          marginTop: 3,
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack spacing={3}>
            <CampaignCreatorInfo
              campaign={contentSubmission?.campaign}
              profile={contentSubmission?.profile}
            />

            <Accordion defaultExpanded disableGutters elevation={0}>
              <AccordionSummary expandIcon={expandIcon}>
                <Headline
                  size="sm"
                  color={designSystemToken('semantic.fg.primary')}
                >
                  Campaign Fit Score
                </Headline>
              </AccordionSummary>
              <StyledAccordionDetails>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    marginTop: 2,
                  }}
                >
                  <Headline
                    size="xl"
                    sx={{
                      color:
                        score !== undefined ? getScoreColor(score) : undefined,
                      fontWeight: 'bold',
                    }}
                  >
                    {score !== undefined ? score.toFixed(1) : 'N/A'}
                  </Headline>
                  <Body
                    size="lg"
                    color={designSystemToken('semantic.fg.secondary')}
                  >
                    out of 10
                  </Body>
                </Box>
              </StyledAccordionDetails>
            </Accordion>

            {keyStrengths.length > 0 && (
              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={expandIcon}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon
                      name="Actions-Accept"
                      size="medium"
                      appearance="primary"
                    />
                    <Headline
                      size="sm"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      Key Strengths
                    </Headline>
                  </Box>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <Stack spacing={1} sx={{marginTop: 1}}>
                    {keyStrengths.map((strength, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Box key={index} sx={{display: 'flex', gap: 1}}>
                        <Body
                          size="md"
                          color={designSystemToken('semantic.fg.primary')}
                        >
                          • {strength}
                        </Body>
                      </Box>
                    ))}
                  </Stack>
                </StyledAccordionDetails>
              </Accordion>
            )}

            {contentMandatoriesChecklist.length > 0 && (
              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={expandIcon}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon
                      name="Actions-Clipboard-General"
                      size="medium"
                      appearance="primary"
                    />
                    <Headline
                      size="sm"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      Content Mandatories
                    </Headline>
                  </Box>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <Stack spacing={1} sx={{marginTop: 1}}>
                    {contentMandatoriesChecklist.map((item, index) => (
                      <Box
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        sx={{display: 'flex', gap: 1, alignItems: 'flex-start'}}
                      >
                        <Body
                          size="md"
                          sx={{
                            color: item.fulfilled
                              ? designSystemToken('semantic.fg.success')
                              : designSystemToken('semantic.fg.danger'),
                            flexShrink: 0,
                          }}
                        >
                          {item.fulfilled ? '\u2705' : '\u274C'}
                        </Body>
                        <Box>
                          <Body
                            size="md"
                            color={designSystemToken('semantic.fg.primary')}
                          >
                            {item.requirement}
                          </Body>
                          <Body
                            size="sm"
                            color={designSystemToken('semantic.fg.secondary')}
                          >
                            {item.explanation}
                          </Body>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                </StyledAccordionDetails>
              </Accordion>
            )}

            {hasIssues && (
              <Accordion
                defaultExpanded={hasIssues}
                disableGutters
                elevation={0}
                sx={{
                  backgroundColor: designSystemToken(
                    'semantic.bg.warning-secondary'
                  ),
                  borderRadius: 1,
                }}
              >
                <AccordionSummary expandIcon={expandIcon}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon
                      name="Actions-Alert-Line"
                      size="medium"
                      appearance="danger"
                    />
                    <Headline
                      size="sm"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      Analysis Issues
                    </Headline>
                  </Box>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <Stack spacing={1} sx={{marginTop: 1}}>
                    {analysisIssuesList}
                  </Stack>
                </StyledAccordionDetails>
              </Accordion>
            )}

            {[
              {title: 'Theme Relevance', content: themeRelevance},
              {title: 'Visual Style Fit', content: visualStyleFit},
              {title: 'Alignment Summary', content: alignmentSummary},
              {title: 'Audience Alignment', content: audienceAlignment},
            ]
              .filter((section) => section.content)
              .map((section) => (
                <Accordion key={section.title} disableGutters elevation={0}>
                  <AccordionSummary expandIcon={expandIcon}>
                    <Headline
                      size="sm"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      {section.title}
                    </Headline>
                  </AccordionSummary>
                  <StyledAccordionDetails>
                    <Body
                      size="md"
                      color={designSystemToken('semantic.fg.primary')}
                      sx={{marginTop: 1}}
                    >
                      {section.content}
                    </Body>
                  </StyledAccordionDetails>
                </Accordion>
              ))}

            {areasForImprovement.length > 0 && (
              <Accordion disableGutters elevation={0}>
                <AccordionSummary expandIcon={expandIcon}>
                  <Headline
                    size="sm"
                    color={designSystemToken('semantic.fg.primary')}
                  >
                    Areas for Improvement
                  </Headline>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <Stack spacing={1} sx={{marginTop: 1}}>
                    {areasForImprovement.map((area, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Box key={index} sx={{display: 'flex', gap: 1}}>
                        <Body
                          size="md"
                          color={designSystemToken('semantic.fg.primary')}
                        >
                          • {area}
                        </Body>
                      </Box>
                    ))}
                  </Stack>
                </StyledAccordionDetails>
              </Accordion>
            )}

            {creatorFeedbackMessage.length > 0 && (
              <Accordion defaultExpanded disableGutters elevation={0}>
                <AccordionSummary expandIcon={expandIcon}>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Icon
                      name="Actions-Send"
                      size="medium"
                      appearance="primary"
                    />
                    <Headline
                      size="sm"
                      color={designSystemToken('semantic.fg.primary')}
                    >
                      Creator Message
                    </Headline>
                  </Box>
                </AccordionSummary>
                <StyledAccordionDetails>
                  <Box
                    sx={{
                      marginTop: 1,
                      padding: 2,
                      backgroundColor: designSystemToken(
                        'semantic.bg.secondary'
                      ),
                      borderRadius: 1,
                    }}
                  >
                    <Body
                      size="md"
                      color={designSystemToken('semantic.fg.primary')}
                      sx={{whiteSpace: 'pre-wrap'}}
                    >
                      {creatorFeedbackMessage}
                    </Body>
                  </Box>
                  <Box sx={{marginTop: '8px'}}>
                    <Button
                      size="small"
                      appearance="neutral"
                      mode="tinted"
                      onClick={handleCopyMessage}
                    >
                      {copied ? 'Copied!' : 'Copy Message'}
                    </Button>
                  </Box>
                </StyledAccordionDetails>
              </Accordion>
            )}

            {analysis.analysisResult?.analysisMetadata?.llm && (
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                sx={{marginTop: 2, fontStyle: 'italic'}}
              >
                Analysis powered by{' '}
                {analysis.analysisResult.analysisMetadata.llm}
              </Body>
            )}
          </Stack>
        </Box>

        {mediaUrl && (isVideo || isImage) && (
          <Box
            sx={{
              flex: '0 0 400px',
              position: 'sticky',
              top: 20,
              alignSelf: 'flex-start',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                backgroundColor: designSystemToken('semantic.bg.tertiary'),
                cursor: isVideo ? 'pointer' : 'default',
              }}
            >
              {isVideo ? (
                <video
                  ref={videoRef}
                  src={mediaUrl}
                  poster={thumbnailUrl || undefined}
                  controls
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                >
                  <track kind="captions" />
                </video>
              ) : (
                <img
                  src={mediaUrl}
                  alt="Content submission"
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                />
              )}
            </Box>
            {isVideo && (
              <Body
                size="sm"
                color={designSystemToken('semantic.fg.secondary')}
                sx={{
                  marginTop: 1,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Click to watch the content
              </Body>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function formatTimestampSeconds(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const parseTimestampToSeconds = (timestamp: string): number => {
  const parts = timestamp.split(':');
  if (parts.length === 2) {
    const minutes = parseInt(parts[0], 10);
    const seconds = parseInt(parts[1], 10);
    return minutes * 60 + seconds;
  }
  return 0;
};

export default AnalysisContent;
